import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Papa from "papaparse";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";
import { keywordVolumeService, type BrandKeywordMismatch } from "@/services/KeywordVolumeService";

type Kind = BrandKeywordMismatch["kind"];

const KINDS: { id: Kind; label: string; hint: string }[] = [
  { id: "different_brand", label: "Different brand", hint: "Jetsik and the Domain Tracker name different brands" },
  { id: "no_keyword", label: "No brand keyword", hint: "Jetsik knows the brand, the Domain Tracker has no keyword" },
  { id: "contested", label: "Contested", hint: "The keyword differs from the one most of the brand's domains use" },
];

/**
 * Where Jetsik brands and Domain Tracker brand keywords disagree - a work list
 * for the SEO team. Spelling alone (spaces, "Casino") is not listed; it is
 * already matched (brand_key in migration 20260929160000).
 */
export function BrandMismatches() {
  const [kind, setKind] = useState<Kind>("different_brand");
  const { data = [], isPending, error } = useQuery({
    queryKey: ["brandKeywordMismatches"],
    queryFn: () => keywordVolumeService.getBrandMismatches(),
    staleTime: 1000 * 60 * 5,
  });

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of data) c[r.kind] = (c[r.kind] ?? 0) + 1;
    return c;
  }, [data]);
  const rows = data.filter((r) => r.kind === kind);

  const download = () => {
    const blob = new Blob([Papa.unparse(data)], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "brand-keyword-mismatches.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        {KINDS.map((k) => (
          <button
            key={k.id}
            type="button"
            title={k.hint}
            onClick={() => setKind(k.id)}
            className={cn(
              "cursor-pointer rounded-md px-3 py-1 text-sm font-medium transition-colors",
              kind === k.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/70",
            )}
          >
            {k.label} · {counts[k.id] ?? 0}
          </button>
        ))}
        <Button variant="outline" size="sm" className="ml-auto" disabled={!data.length} onClick={download}>
          Download CSV (all)
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">{KINDS.find((k) => k.id === kind)?.hint}</p>

      {error && <p className="text-sm signal-text-negative">{(error as Error).message}</p>}

      <div className="overflow-x-auto rounded-lg border border-border bg-card">
        <Table>
          <TableHeader className="bg-muted/50">
            <TableRow>
              <TableHead>Domain</TableHead>
              <TableHead>Jetsik brand</TableHead>
              <TableHead>Domain Tracker keyword</TableHead>
              <TableHead>Keyword most of the brand uses</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-muted-foreground">
                  {isPending ? "Loading..." : "Nothing here."}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow key={r.domain}>
                  <TableCell className="font-medium">{r.domain}</TableCell>
                  <TableCell>{r.jetsik_brand}</TableCell>
                  <TableCell>{r.brand_keyword ?? "—"}</TableCell>
                  <TableCell className="text-muted-foreground">{r.brand_voted_keyword ?? "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
